import { createSelector, createSlice } from '@reduxjs/toolkit';

// Dedicated slice for the "Companies > Other" screen — employers who signed up
// outside the provinces/states Staffy services (Admin 1.0's "Other" tab under
// Companies, backed by `otherCitiesCompanies`).
//
// Kept separate from `approvedCompanies` even though the row shape matches: the two
// screens are mounted independently and each owns its own filter bar, so sharing a
// slice would leak one screen's filters into the other.
const otherCompaniesSlice = createSlice({
    name: 'otherCompanies',
    initialState: {
        filters: [],
        companies: [],
        totalRecords: 0,
        isLoading: false,
    },
    reducers: {
        fetchOtherCompanies: state => {
            state.companies = [];
            state.isLoading = true;
        },
        otherCompaniesFetched: (state, { payload }) => {
            const { results, totalRecords } = payload;
            state.companies = results;
            state.totalRecords = totalRecords;
            state.isLoading = false;
        },
        otherCompaniesFetchRequestFailed: state => {
            state.isLoading = false;
        },
        // Removes a row locally (e.g. after Delete / Reject) without a refetch.
        removeOtherCompany: (state, { payload }) => {
            state.companies = state.companies.filter(c => c._id !== payload.id);
        },
        // Shallow-merges updated fields into a single row (e.g. after Accept or
        // Edit Profile).
        otherCompanyPartialUpdate: (state, { payload }) => {
            const { userId, updatedData } = payload;
            state.companies = state.companies.map(company =>
                company._id === userId
                    ? { ...company, ...updatedData }
                    : company,
            );
        },
        // Same filter-array contract as the talent / approvedCompanies slices.
        upsertFilter: (state, { payload }) => {
            const { key, value } = payload;
            const index = state.filters.findIndex(f => f.key === key);

            if (index === -1 && value === '') {
                return;
            }

            if (index === -1) {
                state.filters.push(payload);
            } else {
                state.filters[index].value = value;
            }
        },
        removeFilter: (state, { payload }) => {
            const index = state.filters.findIndex(f => f.key === payload);
            if (index !== -1) {
                state.filters.splice(index, 1);
            }
        },
        removeAllFilters: state => {
            state.filters = [];
        },
    },
});

// SELECTORS
const getSelf = state => state.otherCompanies;

export const getOtherCompanies = createSelector(
    getSelf,
    state => state.companies,
);
export const getFilters = createSelector(getSelf, state => state.filters);
export const isLoading = createSelector(getSelf, state => state.isLoading);
export const getTotalRecords = createSelector(
    getSelf,
    state => state.totalRecords,
);

export const {
    fetchOtherCompanies,
    otherCompaniesFetched,
    otherCompaniesFetchRequestFailed,
    removeOtherCompany,
    otherCompanyPartialUpdate,
    upsertFilter,
    removeFilter,
    removeAllFilters,
} = otherCompaniesSlice.actions;

export default otherCompaniesSlice.reducer;
